import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCenter,
  pointerWithin,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { Icon } from '@iconify/react';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Popover from 'react-bootstrap/Popover';
import { getKkpSurfaceStyle } from '../../utils/kkpColorUtils';
import {
  findCdfPageByKey,
  validateHalfPagePlacement,
} from '../../utils/kkpCdfPlacementRules';
import { showViewportNotice } from '../../utils/showViewportNotice';
import { AppButton } from '../ui';

const MONDE_FOOD_SPREAD_ID = 'var_monde_food';

const isMondeFoodLayoutSpread = (spread) => (
  spread?.id === MONDE_FOOD_SPREAD_ID && spread?.monde_food_layout !== false
);

const spreadHasPlacedPubs = (spread) => {
  const pages = [spread?.left, spread?.right];
  const pageHasPub = pages.some((page) => (
    (page?.slots ?? []).some((slot) => slot.type === 'pub' && slot.vente_id)
  ));
  const doubleHasPub = (spread?.double_slots ?? []).some((slot) => slot.type === 'pub' && slot.vente_id);
  return pageHasPub || doubleHasPub;
};

const isReservedSwapSpread = (spreadId, spreads = []) => {
  if (spreadId === 'var_immo') return true;
  if (spreadId === MONDE_FOOD_SPREAD_ID) {
    const spread = spreads.find((item) => item.id === spreadId);
    return isMondeFoodLayoutSpread(spread || { id: spreadId });
  }
  return false;
};

const computeSpreadReorderTargetIndex = (sourceSpreadId, targetSpreadId, zoneOrder) => {
  const sourceIndex = zoneOrder.indexOf(sourceSpreadId);
  if (sourceIndex < 0) return -1;

  if (targetSpreadId === 'start_0') {
    return 0;
  }
  if (targetSpreadId === 'end_3') {
    return Math.max(0, zoneOrder.length - 1);
  }

  const targetIndex = zoneOrder.indexOf(targetSpreadId);
  if (targetIndex < 0 || sourceIndex === targetIndex) {
    return -1;
  }

  return targetIndex;
};

const computeSpreadInsertIndex = (sourceSpreadId, insertIndex, order) => {
  const sourceIndex = order.indexOf(sourceSpreadId);
  if (sourceIndex < 0 || insertIndex < 0) return -1;
  let insertAt = insertIndex;
  if (sourceIndex < insertAt) insertAt -= 1;
  if (sourceIndex === insertAt) return -1;
  return insertAt;
};

const spreadReorderCollision = (args) => {
  const spreadTargets = args.droppableContainers.filter(
    (container) => container.data.current?.type === 'spread-reorder-target',
  );
  if (spreadTargets.length > 0) {
    const pointerHits = pointerWithin({
      ...args,
      droppableContainers: spreadTargets,
    });
    if (pointerHits.length > 0) {
      return pointerHits;
    }
    const centerHits = closestCenter({
      ...args,
      droppableContainers: spreadTargets,
    });
    if (centerHits.length > 0) {
      return centerHits;
    }
  }

  const insertContainers = args.droppableContainers.filter(
    (container) => container.data.current?.type === 'reorder-insert',
  );
  if (insertContainers.length === 0) {
    return closestCenter(args);
  }

  const pointerHits = pointerWithin({
    ...args,
    droppableContainers: insertContainers,
  });
  if (pointerHits.length > 0) {
    return pointerHits;
  }

  return closestCenter({
    ...args,
    droppableContainers: insertContainers,
  });
};

const collisionDetection = (args) => {
  if (args.active?.data?.current?.type === 'spread') {
    return spreadReorderCollision(args);
  }
  if (args.active?.data?.current?.type === 'page-swap') {
    const pageTargets = args.droppableContainers.filter(
      (container) => container.data.current?.type === 'page-swap-target',
    );
    if (pageTargets.length === 0) {
      return closestCenter(args);
    }
    const pointerHits = pointerWithin({
      ...args,
      droppableContainers: pageTargets,
    });
    if (pointerHits.length > 0) {
      return pointerHits;
    }
    return closestCenter({
      ...args,
      droppableContainers: pageTargets,
    });
  }
  return closestCenter(args);
};

const SpreadInsertZone = ({ insertIndex, reorderZone, activeDrag, style }) => {
  const isSpreadDrag = activeDrag?.type === 'spread';
  const { setNodeRef, isOver } = useDroppable({
    id: `reorder-insert-${reorderZone}-${insertIndex}`,
    data: { type: 'reorder-insert', insertIndex, reorderZone },
    disabled: !isSpreadDrag,
  });

  if (!isSpreadDrag) {
    return null;
  }

  return (
    <div
      ref={setNodeRef}
      className={[
        'kkp-cdf-v2-insert-zone',
        'kkp-cdf-v2-insert-zone--active',
        isOver ? 'kkp-cdf-v2-insert-zone--over' : '',
      ].filter(Boolean).join(' ')}
      style={style}
      title="Déposer ici pour insérer la double page"
    />
  );
};

const SpreadReorderOverlay = ({ magazineRef, spreads, activeDrag, reorderableByZone }) => {
  const [gaps, setGaps] = useState([]);

  useLayoutEffect(() => {
    if (activeDrag?.type !== 'spread' || !magazineRef.current) {
      setGaps([]);
      return undefined;
    }

    const updateGaps = () => {
      const zone = activeDrag.reorderZone;
      const ids = reorderableByZone[zone] || [];
      const magazine = magazineRef.current;
      if (!magazine) return;

      const magRect = magazine.getBoundingClientRect();
      const nextGaps = [];

      ids.forEach((id, index) => {
        const el = magazine.querySelector(`[data-spread-id="${id}"]`);
        if (!el) return;

        const rect = el.getBoundingClientRect();
        if (index === 0) {
          nextGaps.push({
            insertIndex: 0,
            reorderZone: zone,
            style: {
              left: `${rect.left - magRect.left - 12}px`,
              top: `${rect.top - magRect.top}px`,
              height: `${rect.height}px`,
            },
          });
        }

        const nextEl = magazine.querySelector(`[data-spread-id="${ids[index + 1]}"]`);
        if (nextEl) {
          const nextRect = nextEl.getBoundingClientRect();
          nextGaps.push({
            insertIndex: index + 1,
            reorderZone: zone,
            style: {
              left: `${((rect.right + nextRect.left) / 2) - magRect.left - 12}px`,
              top: `${Math.min(rect.top, nextRect.top) - magRect.top}px`,
              height: `${Math.max(rect.height, nextRect.height)}px`,
            },
          });
        } else {
          nextGaps.push({
            insertIndex: index + 1,
            reorderZone: zone,
            style: {
              left: `${rect.right - magRect.left + 4}px`,
              top: `${rect.top - magRect.top}px`,
              height: `${rect.height}px`,
            },
          });
        }
      });

      setGaps(nextGaps);
    };

    updateGaps();
    window.addEventListener('resize', updateGaps);
    window.addEventListener('scroll', updateGaps, true);

    return () => {
      window.removeEventListener('resize', updateGaps);
      window.removeEventListener('scroll', updateGaps, true);
    };
  }, [activeDrag, spreads, magazineRef, reorderableByZone]);

  if (activeDrag?.type !== 'spread') {
    return null;
  }

  return (
    <div className="kkp-cdf-v2-reorder-overlay" aria-hidden={false}>
      {gaps.map((gap) => (
        <SpreadInsertZone
          key={`${gap.reorderZone}-${gap.insertIndex}`}
          insertIndex={gap.insertIndex}
          reorderZone={gap.reorderZone}
          style={gap.style}
          activeDrag={activeDrag}
        />
      ))}
    </div>
  );
};

const gridStyle = ({ col, row, colSpan, rowSpan }) => ({
  gridColumn: `${col} / span ${colSpan}`,
  gridRow: `${row} / span ${rowSpan}`,
});

const stopEditKeys = (event) => {
  event.stopPropagation();
};

const CdfEditorialInput = ({
  value,
  onChange,
  onBlur,
  placeholder = 'Article…',
  className,
}) => (
  <textarea
    className={className}
    value={value}
    onChange={onChange}
    onBlur={onBlur}
    autoFocus
    placeholder={placeholder}
    onClick={(event) => event.stopPropagation()}
    onPointerDown={(event) => event.stopPropagation()}
    onMouseDown={(event) => event.stopPropagation()}
    onKeyDown={stopEditKeys}
    onKeyUp={stopEditKeys}
  />
);

const CdfLibreEmpty = ({ label = 'Libre' }) => (
  <span className="kkp-cdf-v2-slot__libre-empty">
    <Icon icon="mdi:vector-rectangle" className="kkp-cdf-v2-slot__libre-icon" aria-hidden />
    <span className="kkp-cdf-v2-slot__libre-label">{label}</span>
  </span>
);

const PlacementPopover = ({ vente, children }) => (
  <OverlayTrigger
    trigger={['hover', 'focus']}
    placement="auto"
    delay={{ show: 200, hide: 100 }}
    overlay={(
      <Popover id={`cdf-v2-pop-${vente.vente_id}`} className="kkp-cdf-popover">
        <Popover.Header as="div" className="fw-semibold">
          {vente.format}
          {vente.statut_phase && (
            <span className="text-secondary-light fw-normal ms-2">· {vente.statut_phase}</span>
          )}
        </Popover.Header>
        <Popover.Body className="small">
          {vente.contenu && (
            <div className="mb-1"><strong>Contenu :</strong> {vente.contenu}</div>
          )}
          <div className="mb-1"><strong>Commercial :</strong> {vente.commercial || '—'}</div>
          <div className="mb-1"><strong>Client :</strong> {vente.client_label}</div>
          {vente.commentaire && (
            <div className="mb-1"><strong>Commentaire :</strong> {vente.commentaire}</div>
          )}
          <div className="text-secondary-light mt-2">Glisser pour déplacer · Cliquer pour modifier</div>
        </Popover.Body>
      </Popover>
    )}
  >
    <span className="kkp-cdf-v2-slot__wrap">{children}</span>
  </OverlayTrigger>
);

const toSentenceCase = (value) => {
  const s = String(value ?? '').trim();
  if (!s) return '';
  const lower = s.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

const PubCard = ({ vente, venteId, compact = false }) => {
  if (!vente) {
    return (
      <div className="kkp-cdf-v2-pub kkp-cdf-v2-pub--placeholder">
        <div className="kkp-cdf-v2-pub__client">Pub #{venteId || '?'}</div>
      </div>
    );
  }
  const surface = getKkpSurfaceStyle(vente.couleur || vente.statut_couleur);
  const isMondeFood = String(vente.format || '').toUpperCase().includes('MONDE FOOD')
    || String(vente.contenu || '').toUpperCase().includes('MONDE FOOD');
  const contenuNorm = String(vente.contenu || '').toUpperCase().trim();
  const hasTrame = contenuNorm === 'COMMUNIQUE' || contenuNorm === 'PUB REPORTAGE';
  const displayClient = toSentenceCase(
    vente.dolibarr_thirdparty_nom || vente.entreprise_nom || vente.client_label || '',
  );
  const displayFormat = toSentenceCase(vente.format);
  const displayContenu = toSentenceCase(vente.contenu);

  return (
    <div
      className={[
        'kkp-cdf-v2-pub',
        compact && isMondeFood ? 'kkp-cdf-v2-pub--compact' : '',
        hasTrame ? 'kkp-cdf-v2-pub--trame' : '',
      ].filter(Boolean).join(' ')}
      style={{
        backgroundColor: surface.backgroundColor,
        color: surface.color,
      }}
    >
      {compact && isMondeFood ? (
        <>
          <div className="kkp-cdf-v2-pub__client">{displayClient || toSentenceCase(vente.client_label)}</div>
          <div className="kkp-cdf-v2-pub__format kkp-cdf-v2-pub__format--compact">{displayFormat}</div>
        </>
      ) : (
        <>
          <div className="kkp-cdf-v2-pub__client">{displayClient || toSentenceCase(vente.client_label)}</div>
          <div className="kkp-cdf-v2-pub__format">{displayFormat}</div>
          {displayContenu && (
            <div className="kkp-cdf-v2-pub__contenu" style={{ color: surface.muted }}>
              {displayContenu}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const UnplacedChip = ({ vente, disabled }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `unplaced-${vente.vente_id}`,
    data: { type: 'pub', venteId: vente.vente_id, vente },
    disabled,
  });

  return (
    <div
      ref={setNodeRef}
      className={`kkp-cdf-v2-chip${isDragging ? ' opacity-50' : ''}`}
      {...listeners}
      {...attributes}
    >
      <span className="text-secondary-light">{vente.format}</span>
      <span className="kkp-cdf-v2-chip__client">{vente.client_label}</span>
    </div>
  );
};

const SlotCell = ({
  slot,
  spreadId,
  pageKey,
  page,
  onPlacementClick,
  onLibreClick,
  onArticleSave,
  onArticleDraftChange,
  onEditStart,
  onEditEnd,
  activeDrag,
  isGlobalEditing,
}) => {
  const isPub = slot.type === 'pub' && slot.vente_id;
  const isLibre = slot.type === 'libre' || (slot.type === 'pub' && !slot.vente_id);
  const isLocked = Boolean(slot.vente?.locked || slot.locked);
  const gridRows = page?.grid_rows ?? 2;
  const gridCols = page?.grid_cols ?? 2;
  const isCompactPub = (gridRows === 2 && gridCols === 2 && (slot.colSpan < 2 || slot.rowSpan < 2))
    || (gridRows === 3 && gridCols === 2)
    || (gridRows === 4 && gridCols === 4 && slot.rowSpan <= 1);
  const dropId = `drop-${pageKey}-${slot.id}`;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(page?.article_text || '');
  const committedTextRef = useRef(page?.article_text || '');

  const isSpreadDrag = activeDrag?.type === 'spread';
  const isPageSwapDrag = activeDrag?.type === 'page-swap';
  const isPubDrag = activeDrag?.type === 'pub';

  const isArticleSlot = String(slot.id ?? '').endsWith('_article');
  const isMondeFoodPubGrid = gridRows === 4 && gridCols === 4;
  const canEditArticle = isLibre && (!isMondeFoodPubGrid || isArticleSlot);
  const hasArticleText = canEditArticle && Boolean((page?.article_text || '').trim());
  const targetPubLocked = Boolean(slot.vente?.locked || slot.locked);
  // Pages standard : dépôt sur une autre pub = échange (même taille, 1/2↔2×1/4, 1/2↔PAGE, PAGE↔contenu).
  // PAGE sur un libre d’une page déjà occupée est aussi traité comme échange côté API.
  const canAcceptPubDrop = isLibre
    || (isPub && !isArticleSlot && !targetPubLocked);

  useEffect(() => {
    if (!editing) {
      const next = page?.article_text || '';
      setDraft(next);
      committedTextRef.current = next;
    }
  }, [page?.article_text, editing]);

  const { setNodeRef, isOver } = useDroppable({
    id: dropId,
    data: {
      type: 'slot',
      spreadId,
      pageKey,
      slotId: slot.id,
      slotType: slot.type,
      venteId: slot.vente_id,
    },
    disabled: isSpreadDrag || isPageSwapDrag || (isPubDrag && !canAcceptPubDrop),
  });

  const dragId = isPub ? `pub-${slot.vente_id}` : `static-${slot.id}`;
  const {
    attributes,
    listeners,
    setNodeRef: setDragRef,
    isDragging,
  } = useDraggable({
    id: dragId,
    data: {
      type: 'pub',
      venteId: slot.vente_id,
      vente: slot.vente,
      spreadId,
      pageKey,
    },
    disabled: !isPub || isLocked || isGlobalEditing,
  });

  const setRef = (node) => {
    setNodeRef(node);
    if (isPub) setDragRef(node);
  };

  const handleClick = (e) => {
    e.stopPropagation();
    if (isPub && slot.vente) {
      onPlacementClick(slot.vente);
    }
  };

  const handleLibreAdd = (e) => {
    e.stopPropagation();
    onLibreClick({ spreadId, pageKey });
  };

  const handleDoubleClick = (e) => {
    if (!canEditArticle) return;
    e.stopPropagation();
    const current = page?.article_text || '';
    setDraft(current);
    committedTextRef.current = current;
    setEditing(true);
    onEditStart?.(pageKey);
  };

  const commitArticle = () => {
    setEditing(false);
    onEditEnd?.();
    if (draft !== committedTextRef.current) {
      onArticleSave(pageKey, draft);
    }
  };

  const handleDraftChange = (e) => {
    const value = e.target.value;
    setDraft(value);
    onArticleDraftChange?.(value);
  };

  const cancelEditListeners = editing || isGlobalEditing;

  const showDropTarget = isOver && isPubDrag
    && activeDrag?.venteId !== slot.vente_id
    && canAcceptPubDrop;

  return (
    <div
      ref={setRef}
      className={[
        'kkp-cdf-v2-slot',
        isLibre ? 'kkp-cdf-v2-slot--libre' : 'kkp-cdf-v2-slot--pub',
        isLibre && hasArticleText && !editing ? 'kkp-cdf-v2-slot--libre-editorial' : '',
        showDropTarget ? 'kkp-cdf-v2-slot--drop-target' : '',
        isDragging ? 'kkp-cdf-v2-slot--dragging' : '',
      ].filter(Boolean).join(' ')}
      style={gridStyle(slot)}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      role={editing ? undefined : 'button'}
      tabIndex={editing ? undefined : 0}
      onKeyDown={(e) => {
        if (editing || e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLInputElement) {
          e.stopPropagation();
          return;
        }
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick(e);
        }
      }}
      {...(isPub && !isLocked && !cancelEditListeners ? { ...listeners, ...attributes } : {})}
    >
      {isPub ? (
        slot.vente ? (
          <PlacementPopover vente={slot.vente}>
            <PubCard
              vente={slot.vente}
              venteId={slot.vente_id}
              compact={isCompactPub}
            />
          </PlacementPopover>
        ) : (
          <PubCard
            vente={null}
            venteId={slot.vente_id}
            compact={isCompactPub}
          />
        )
      ) : (
        <>
          {editing ? (
            <CdfEditorialInput
              className="kkp-cdf-v2-slot__editorial-input"
              value={draft}
              onChange={handleDraftChange}
              onBlur={commitArticle}
              placeholder="Notes…"
            />
          ) : (
            <>
              {hasArticleText ? (
                <div className="kkp-cdf-v2-slot__libre-preview">{page.article_text}</div>
              ) : (
                <CdfLibreEmpty />
              )}
            </>
          )}
          {!editing && (
            <button
              type="button"
              className="kkp-cdf-v2-slot__add-btn"
              title="Nouvelle vente"
              onClick={handleLibreAdd}
              onPointerDown={(e) => e.stopPropagation()}
            >
              <Icon icon="mdi:plus" />
            </button>
          )}
        </>
      )}
    </div>
  );
};

const PageSlots = ({
  page,
  spreadId,
  onPlacementClick,
  onLibreClick,
  onArticleSave,
  onArticleDraftChange,
  onEditStart,
  onEditEnd,
  activeDrag,
  isGlobalEditing,
  className = 'kkp-cdf-v2-page__grid',
}) => {
  const gridRows = page.grid_rows ?? 2;
  const gridCols = page.grid_cols ?? 2;

  return (
  <div
    className={[
      className,
      gridRows === 3 && gridCols === 2 ? 'kkp-cdf-v2-page__grid--tiers' : '',
      gridCols === 4 ? 'kkp-cdf-v2-page__grid--monde-food' : '',
    ].filter(Boolean).join(' ')}
    style={{
      gridTemplateRows: `repeat(${gridRows}, 1fr)`,
      gridTemplateColumns: `repeat(${gridCols}, 1fr)`,
    }}
  >
    {(page.slots ?? []).map((slot) => (
      <SlotCell
        key={slot.id}
        slot={slot}
        spreadId={spreadId}
        pageKey={page.key}
        page={page}
        onPlacementClick={onPlacementClick}
        onLibreClick={onLibreClick}
        onArticleSave={onArticleSave}
        onArticleDraftChange={onArticleDraftChange}
        onEditStart={onEditStart}
        onEditEnd={onEditEnd}
        activeDrag={activeDrag}
        isGlobalEditing={isGlobalEditing}
      />
    ))}
  </div>
  );
};

const PageSide = ({
  page,
  spreadId,
  onPlacementClick,
  onLibreClick,
  onArticleSave,
  onArticleDraftChange,
  onOptionalAgendaToggle,
  onEditStart,
  onEditEnd,
  activeDrag,
  isGlobalEditing,
  hideSlots = false,
  fullPage = false,
  hidePageHeader = false,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(page.article_text || '');

  const isSpreadDrag = activeDrag?.type === 'spread';
  const isPageSwapDrag = activeDrag?.type === 'page-swap';
  const isPubDrag = activeDrag?.type === 'pub';
  const hasSlotGrid = (page.slots ?? []).length > 0;

  const {
    attributes: swapDragAttributes,
    listeners: swapDragListeners,
    setNodeRef: setSwapDragRef,
    isDragging: isPageDragging,
  } = useDraggable({
    id: `page-drag-${page.key}`,
    data: {
      type: 'page-swap',
      pageKey: page.key,
      spreadId,
      pageMode: page.mode,
      pageNumber: page.page_number,
      defaultLabel: page.default_label,
    },
    disabled: !page.swappable || isGlobalEditing,
  });

  const { setNodeRef: setSwapDropRef, isOver: isSwapOver } = useDroppable({
    id: `page-drop-${page.key}`,
    data: {
      type: 'page-swap-target',
      pageKey: page.key,
      spreadId,
      pageMode: page.mode,
    },
    disabled: !page.swappable || isGlobalEditing,
  });

  const { setNodeRef, isOver } = useDroppable({
    id: `page-${page.key}`,
    data: { type: 'page', spreadId, pageKey: page.key },
    disabled: !page.droppable || isSpreadDrag || isPageSwapDrag || hasSlotGrid || isGlobalEditing,
  });

  const mergePageRefs = (node) => {
    if (page.swappable) {
      setSwapDropRef(node);
    }
    if (page.droppable && !hasSlotGrid) {
      setNodeRef(node);
    }
  };

  const pageSwapClass = [
    page.swappable ? 'kkp-cdf-v2-page--swappable' : '',
    isPageDragging ? 'kkp-cdf-v2-page--swap-dragging' : '',
    isSwapOver && isPageSwapDrag ? 'kkp-cdf-v2-page--swap-over' : '',
  ].filter(Boolean).join(' ');

  const pageSwapHandle = page.swappable && !isGlobalEditing ? (
    <button
      type="button"
      ref={setSwapDragRef}
      className="kkp-cdf-v2-page__swap-handle"
      title="Glisser pour échanger cette page"
      onPointerDown={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      {...swapDragListeners}
      {...swapDragAttributes}
    >
      <Icon icon="mdi:swap-horizontal" />
    </button>
  ) : null;

  const optionalAgendaHandle = page.optional_agenda && onOptionalAgendaToggle && !isGlobalEditing ? (
    <button
      type="button"
      className={[
        'kkp-cdf-v2-page__optional-agenda',
        page.optional_agenda_enabled
          ? 'kkp-cdf-v2-page__optional-agenda--remove'
          : 'kkp-cdf-v2-page__optional-agenda--restore',
      ].join(' ')}
      title={page.optional_agenda_enabled
        ? 'Retirer la page Agenda (devient une page pub)'
        : 'Restaurer la page Agenda'}
      onClick={(e) => {
        e.stopPropagation();
        onOptionalAgendaToggle(!page.optional_agenda_enabled);
      }}
      onPointerDown={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <Icon icon={page.optional_agenda_enabled ? 'mdi:close' : 'mdi:calendar-plus'} />
    </button>
  ) : null;

  const handleDoubleClick = (e) => {
    if (page.mode === 'editorial' || page.mode === 'pub' || page.mode === 'horoscope' || page.mode === 'agenda') {
      e.stopPropagation();
      setDraft(page.article_text || '');
      setEditing(true);
      onEditStart?.(page.key);
    }
  };

  const commitArticle = () => {
    setEditing(false);
    onEditEnd?.();
    if (draft !== (page.article_text || '')) {
      onArticleSave(page.key, draft);
    }
  };

  const showDropTarget = isOver && activeDrag?.type === 'pub';
  const showPageSwapTarget = isSwapOver && isPageSwapDrag && activeDrag?.pageKey !== page.key;

  if (page.mode === 'cover') {
    return (
      <div className="kkp-cdf-v2-page kkp-cdf-v2-page--cover">
        <div className="kkp-cdf-v2-page__header">p.{page.page_number}</div>
        <div className="kkp-cdf-v2-cover-page">
          <span className="kkp-cdf-v2-cover">{page.default_label}</span>
        </div>
      </div>
    );
  }

  if (page.mode === 'horoscope' || page.mode === 'agenda') {
    const pubSlot = (page.slots ?? []).find((s) => s.type === 'pub');

    return (
      <div
        ref={page.swappable ? mergePageRefs : undefined}
        className={[
          'kkp-cdf-v2-page',
          'kkp-cdf-v2-page--horoscope',
          pageSwapClass,
          showPageSwapTarget ? 'kkp-cdf-v2-page--swap-over' : '',
        ].filter(Boolean).join(' ')}
        onPointerDown={(e) => editing && e.stopPropagation()}
        onKeyDown={(e) => editing && e.stopPropagation()}
      >
        {pageSwapHandle}
        {optionalAgendaHandle}
        <div
          className="kkp-cdf-v2-page__horoscope-editorial"
          onDoubleClick={handleDoubleClick}
        >
          {page.default_label && (
            <span className="kkp-cdf-v2-page__editorial-label">{page.default_label}</span>
          )}
          {editing ? (
            <CdfEditorialInput
              className="kkp-cdf-v2-page__editorial-input"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={commitArticle}
            />
          ) : (
            page.article_text && (
              <div className="kkp-cdf-v2-page__editorial-text">{page.article_text}</div>
            )
          )}
        </div>
        <div className="kkp-cdf-v2-page__horoscope-bandeau">
          {pubSlot ? (
            <SlotCell
              slot={{ ...pubSlot, col: 1, row: 1, colSpan: 2, rowSpan: 2 }}
              spreadId={spreadId}
              pageKey={page.key}
              page={page}
              onPlacementClick={onPlacementClick}
              onLibreClick={onLibreClick}
              onArticleSave={onArticleSave}
              onArticleDraftChange={onArticleDraftChange}
              onEditStart={onEditStart}
              onEditEnd={onEditEnd}
              activeDrag={activeDrag}
              isGlobalEditing={isGlobalEditing}
            />
          ) : page.mode === 'agenda' ? (
            <div
              className="kkp-cdf-v2-slot kkp-cdf-v2-slot--libre kkp-cdf-v2-slot--agenda-reserve"
              role="button"
              tabIndex={isGlobalEditing ? -1 : 0}
              title="Ajouter le bandeau agenda"
              onClick={(e) => {
                e.stopPropagation();
                if (isGlobalEditing) return;
                if (page.reserved_vente_id) {
                  onPlacementClick?.({ vente_id: page.reserved_vente_id });
                  return;
                }
                onLibreClick?.({
                  format: 'Bandeau agenda',
                  contenu: 'PUBLICITE',
                  lockFormat: true,
                });
              }}
              onKeyDown={(e) => {
                if (e.key !== 'Enter' && e.key !== ' ') return;
                e.preventDefault();
                e.currentTarget.click();
              }}
            >
              <CdfLibreEmpty label="Bandeau agenda" />
              {!isGlobalEditing && (
                <span className="kkp-cdf-v2-slot__add-btn" aria-hidden>
                  <Icon icon="mdi:plus" />
                </span>
              )}
            </div>
          ) : (
            <div className="kkp-cdf-v2-slot kkp-cdf-v2-slot--libre">
              <CdfLibreEmpty />
            </div>
          )}
        </div>
      </div>
    );
  }

  if (page.mode === 'couv_pub') {
    return (
      <div className="kkp-cdf-v2-page">
        <PageSlots
          page={page}
          spreadId={spreadId}
          onPlacementClick={onPlacementClick}
          onLibreClick={onLibreClick}
          onArticleSave={onArticleSave}
          onArticleDraftChange={onArticleDraftChange}
          onEditStart={onEditStart}
          onEditEnd={onEditEnd}
          activeDrag={activeDrag}
          isGlobalEditing={isGlobalEditing}
          className="kkp-cdf-v2-page__grid"
        />
      </div>
    );
  }

  if (page.mode === 'editorial' && (hideSlots || fullPage)) {
    return (
      <div
        ref={page.swappable ? mergePageRefs : undefined}
        className={[
          'kkp-cdf-v2-page',
          'kkp-cdf-v2-page--editorial-full',
          pageSwapClass,
          showPageSwapTarget ? 'kkp-cdf-v2-page--swap-over' : '',
        ].filter(Boolean).join(' ')}
        onDoubleClick={handleDoubleClick}
        onPointerDown={(e) => editing && e.stopPropagation()}
        onKeyDown={(e) => editing && e.stopPropagation()}
      >
        {pageSwapHandle}
        {optionalAgendaHandle}
        <div className="kkp-cdf-v2-page__editorial kkp-cdf-v2-page__editorial--full">
          {page.default_label && (
            <span className="kkp-cdf-v2-page__editorial-label">{page.default_label}</span>
          )}
          {editing ? (
            <CdfEditorialInput
              className="kkp-cdf-v2-page__editorial-input"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={commitArticle}
            />
          ) : (
            page.article_text && (
              <div className="kkp-cdf-v2-page__editorial-text">{page.article_text}</div>
            )
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={page.swappable || (page.droppable && !hasSlotGrid) ? mergePageRefs : undefined}
      className={[
        'kkp-cdf-v2-page',
        showDropTarget ? 'kkp-cdf-v2-page--drop-target' : '',
        pageSwapClass,
        showPageSwapTarget ? 'kkp-cdf-v2-page--swap-over' : '',
      ].filter(Boolean).join(' ')}
      onDoubleClick={handleDoubleClick}
      onPointerDown={(e) => editing && e.stopPropagation()}
      onKeyDown={(e) => editing && e.stopPropagation()}
    >
      {pageSwapHandle}
      {optionalAgendaHandle}
      {!hidePageHeader && (
        <div className="kkp-cdf-v2-page__header">
          p.{page.page_number}
          {page.default_label ? ` · ${page.default_label}` : ''}
        </div>
      )}

      {page.mode === 'editorial' && (
        <div className="kkp-cdf-v2-page__editorial">
          {page.default_label && (
            <span className="kkp-cdf-v2-page__editorial-label">{page.default_label}</span>
          )}
          {editing ? (
            <CdfEditorialInput
              className="kkp-cdf-v2-page__editorial-input"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={commitArticle}
            />
          ) : (
            page.article_text && (
              <div className="kkp-cdf-v2-page__editorial-text">{page.article_text}</div>
            )
          )}
        </div>
      )}

      {!hideSlots && (
        <PageSlots
          page={page}
          spreadId={spreadId}
          onPlacementClick={onPlacementClick}
          onLibreClick={onLibreClick}
          onArticleSave={onArticleSave}
          onArticleDraftChange={onArticleDraftChange}
          onEditStart={onEditStart}
          onEditEnd={onEditEnd}
          activeDrag={activeDrag}
          isGlobalEditing={isGlobalEditing}
          className={page.mode === 'editorial' ? 'kkp-cdf-v2-page__slots' : 'kkp-cdf-v2-page__grid'}
        />
      )}
    </div>
  );
};

const SingleSpreadCard = ({
  spread,
  onPlacementClick,
  onLibreClick,
  onArticleSave,
  onEditStart,
  onEditEnd,
  activeDrag,
  isGlobalEditing,
}) => {
  const isSpreadDrag = activeDrag?.type === 'spread';
  const dragDisabled = !spread.reorderable || isGlobalEditing;

  const {
    attributes,
    listeners,
    setNodeRef: setDragRef,
    isDragging,
  } = useDraggable({
    id: `spread-${spread.id}`,
    data: { type: 'spread', spreadId: spread.id, reorderZone: spread.reorder_zone || 'magazine' },
    disabled: dragDisabled,
  });

  const { setNodeRef: setReorderTargetRef, isOver: isReorderOver } = useDroppable({
    id: `spread-reorder-target-${spread.id}`,
    data: { type: 'spread-reorder-target', spreadId: spread.id },
    disabled: !isSpreadDrag || isGlobalEditing,
  });

  const spreadShellClass = [
    'kkp-cdf-v2-spread',
    'kkp-cdf-v2-spread--single',
    spread.reorderable ? 'kkp-cdf-v2-spread--reorderable' : '',
    isDragging ? 'kkp-cdf-v2-spread--dragging' : '',
    isReorderOver && isSpreadDrag && activeDrag?.spreadId !== spread.id
      ? 'kkp-cdf-v2-spread--reorder-over'
      : '',
  ].filter(Boolean).join(' ');

  const headerProps = spread.reorderable && !isGlobalEditing
    ? { ...listeners, ...attributes }
    : {};

  if (spread.mode === 'cover') {
    return (
      <div className="kkp-cdf-v2-spread kkp-cdf-v2-spread--single">
        <div className="kkp-cdf-v2-spread__header">
          <span>p.{spread.page_number}</span>
        </div>
        <div className="kkp-cdf-v2-spread__single-body">
          <div className="kkp-cdf-v2-page kkp-cdf-v2-page--cover-label">
            <span className="kkp-cdf-v2-cover-label">{spread.label}</span>
          </div>
        </div>
      </div>
    );
  }

  if (spread.mode === 'couv_pub' && spread.page) {
    const isLastCover = spread.id === 'couv_4eme';
    return (
      <div ref={setReorderTargetRef} className={spreadShellClass} data-spread-id={spread.id}>
        <div className="kkp-cdf-v2-spread__header">
          <span>p.{spread.page_number}</span>
        </div>
        <div className="kkp-cdf-v2-spread__single-body">
          {isLastCover && !(spread.page.slots ?? []).some((s) => s.type === 'pub') ? (
            <div className="kkp-cdf-v2-page kkp-cdf-v2-page--cover-label">
              <span className="kkp-cdf-v2-cover-label">{spread.label}</span>
            </div>
          ) : (
            <PageSide
              page={spread.page}
              spreadId={spread.id}
              onPlacementClick={onPlacementClick}
              onLibreClick={onLibreClick}
              onArticleSave={onArticleSave}
              onEditStart={onEditStart}
              onEditEnd={onEditEnd}
              activeDrag={activeDrag}
              isGlobalEditing={isGlobalEditing}
              hidePageHeader
            />
          )}
        </div>
      </div>
    );
  }

  if (!spread.page) {
    return null;
  }

  const isEditorial = spread.mode === 'editorial';

  return (
    <div
      ref={(node) => {
        setReorderTargetRef(node);
      }}
      data-spread-id={spread.id}
      className={[
        spreadShellClass,
        isEditorial ? 'kkp-cdf-v2-spread--single-editorial' : '',
      ].filter(Boolean).join(' ')}
    >
      <div
        ref={spread.reorderable ? setDragRef : undefined}
        className="kkp-cdf-v2-spread__header"
        {...headerProps}
      >
        {spread.reorderable && <Icon icon="mdi:drag" className="kkp-cdf-v2-spread__drag-icon" />}
        <span>p.{spread.page_number}{spread.label ? ` · ${spread.label}` : ''}</span>
      </div>
      <div className="kkp-cdf-v2-spread__single-body">
        <PageSide
          page={spread.page}
          spreadId={spread.id}
          onPlacementClick={onPlacementClick}
          onLibreClick={onLibreClick}
          onArticleSave={onArticleSave}
          onEditStart={onEditStart}
          onEditEnd={onEditEnd}
          activeDrag={activeDrag}
          isGlobalEditing={isGlobalEditing}
          hideSlots={isEditorial}
          fullPage
        />
      </div>
    </div>
  );
};

const SpreadCard = ({
  spread,
  onPlacementClick,
  onLibreClick,
  onArticleSave,
  onOptionalAgendaToggle,
  onMondeFoodLayoutToggle,
  onEditStart,
  onEditEnd,
  activeDrag,
  isGlobalEditing,
}) => {
  const isSingle = spread.type === 'single';
  const isSpreadDrag = activeDrag?.type === 'spread';
  const isPageSwapDrag = activeDrag?.type === 'page-swap';
  const dragDisabled = isSingle || !spread.reorderable || isGlobalEditing;
  const canToggleMondeFood = spread.monde_food_toggle || spread.id === MONDE_FOOD_SPREAD_ID;
  const isMondeFoodLayout = isMondeFoodLayoutSpread(spread);
  const isMondeFoodEmpty = spread.monde_food_empty !== false && !spreadHasPlacedPubs(spread);
  const sharedArticleFromProps = spread.left?.article_text || spread.right?.article_text || '';
  const [linkedArticle, setLinkedArticle] = useState(sharedArticleFromProps);

  useEffect(() => {
    if (isMondeFoodLayout) {
      setLinkedArticle(sharedArticleFromProps);
    }
  }, [isMondeFoodLayout, sharedArticleFromProps]);

  const leftPage = isMondeFoodLayout && spread.left
    ? { ...spread.left, article_text: linkedArticle }
    : spread.left;
  const rightPage = isMondeFoodLayout && spread.right
    ? { ...spread.right, article_text: linkedArticle }
    : spread.right;

  const handleArticleDraftChange = (text) => {
    if (isMondeFoodLayout) {
      setLinkedArticle(text);
    }
  };

  const handleArticleSave = (pageKey, text) => {
    if (isMondeFoodLayout) {
      setLinkedArticle(text);
    }
    onArticleSave(pageKey, text);
  };

  const { setNodeRef: setSpreadDropRef, isOver: isSpreadOver } = useDroppable({
    id: `spread-pub-${spread.id}`,
    data: {
      type: 'page',
      spreadId: spread.id,
      pageKey: `${spread.id}_double`,
    },
    disabled: isSingle || isSpreadDrag || isPageSwapDrag || isGlobalEditing,
  });

  const {
    attributes,
    listeners,
    setNodeRef: setDragRef,
    isDragging,
  } = useDraggable({
    id: `spread-${spread.id}`,
    data: { type: 'spread', spreadId: spread.id, reorderZone: spread.reorder_zone || 'magazine' },
    disabled: dragDisabled,
  });

  const { setNodeRef: setReorderTargetRef, isOver: isReorderOver } = useDroppable({
    id: `spread-reorder-target-${spread.id}`,
    data: { type: 'spread-reorder-target', spreadId: spread.id },
    disabled: !isSpreadDrag || isGlobalEditing,
  });

  if (isSingle) {
    return (
      <SingleSpreadCard
        spread={spread}
        onPlacementClick={onPlacementClick}
        onLibreClick={onLibreClick}
        onArticleSave={onArticleSave}
        onOptionalAgendaToggle={onOptionalAgendaToggle}
        onEditStart={onEditStart}
        onEditEnd={onEditEnd}
        activeDrag={activeDrag}
        isGlobalEditing={isGlobalEditing}
      />
    );
  }

  const setCardRef = (node) => {
    setReorderTargetRef(node);
  };

  const setBodyRef = (node) => {
    setSpreadDropRef(node);
  };

  const hasDoublePub = (spread.double_slots ?? []).some((s) => s.type === 'pub');
  const showSpreadDrop = isSpreadOver && activeDrag?.type === 'pub'
    && activeDrag?.vente?.format === 'DOUBLE PAGE';
  const showReorderTarget = isReorderOver && isSpreadDrag
    && activeDrag?.spreadId !== spread.id;

  return (
    <div
      ref={setCardRef}
      data-spread-id={spread.id}
      className={[
        'kkp-cdf-v2-spread',
        spread.reorderable ? 'kkp-cdf-v2-spread--reorderable' : '',
        isDragging ? 'kkp-cdf-v2-spread--dragging' : '',
        showReorderTarget ? 'kkp-cdf-v2-spread--reorder-over' : '',
      ].filter(Boolean).join(' ')}
    >
      <div
        ref={spread.reorderable ? setDragRef : undefined}
        className="kkp-cdf-v2-spread__header"
        {...(spread.reorderable && !isGlobalEditing ? { ...listeners, ...attributes } : {})}
      >
        {spread.reorderable && <Icon icon="mdi:drag" className="kkp-cdf-v2-spread__drag-icon" />}
        <span className="kkp-cdf-v2-spread__title">{spread.title}</span>
        {canToggleMondeFood && onMondeFoodLayoutToggle && !isGlobalEditing && (
          <AppButton
            size="sm"
            variant="outline-secondary"
            className="kkp-cdf-v2-spread__monde-food-toggle"
            disabled={!isMondeFoodEmpty}
            title={isMondeFoodEmpty
              ? (isMondeFoodLayout
                ? 'Transformer en double page normale'
                : 'Restaurer la double page Monde Food')
              : 'Videz la double page avant de changer de mise en page'}
            onClick={(e) => {
              e.stopPropagation();
              if (!isMondeFoodEmpty) return;
              onMondeFoodLayoutToggle(!isMondeFoodLayout);
            }}
            onPointerDown={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {isMondeFoodLayout ? 'Page normale' : 'Monde Food'}
          </AppButton>
        )}
      </div>
      <div ref={setBodyRef} className="kkp-cdf-v2-spread__body">
        {showSpreadDrop && !hasDoublePub && (
          <div className="kkp-cdf-v2-spread__double-overlay kkp-cdf-v2-spread__double-overlay--active" />
        )}
        {leftPage && (
          <PageSide
            page={leftPage}
            spreadId={spread.id}
            onPlacementClick={onPlacementClick}
            onLibreClick={onLibreClick}
            onArticleSave={handleArticleSave}
            onArticleDraftChange={handleArticleDraftChange}
            onOptionalAgendaToggle={onOptionalAgendaToggle}
            onEditStart={onEditStart}
            onEditEnd={onEditEnd}
            activeDrag={activeDrag}
            isGlobalEditing={isGlobalEditing}
            hideSlots={hasDoublePub || leftPage.mode === 'editorial' || leftPage.mode === 'horoscope' || leftPage.mode === 'agenda'}
            fullPage={leftPage.mode === 'editorial'}
            hidePageHeader
          />
        )}
        {rightPage && (
          <PageSide
            page={rightPage}
            spreadId={spread.id}
            onPlacementClick={onPlacementClick}
            onLibreClick={onLibreClick}
            onArticleSave={handleArticleSave}
            onArticleDraftChange={handleArticleDraftChange}
            onOptionalAgendaToggle={onOptionalAgendaToggle}
            onEditStart={onEditStart}
            onEditEnd={onEditEnd}
            activeDrag={activeDrag}
            isGlobalEditing={isGlobalEditing}
            hideSlots={hasDoublePub || rightPage.mode === 'editorial' || rightPage.mode === 'horoscope' || rightPage.mode === 'agenda'}
            fullPage={rightPage.mode === 'editorial'}
            hidePageHeader
          />
        )}
        {hasDoublePub && (
          <div className="kkp-cdf-v2-spread__double-pub">
            {spread.double_slots.map((slot) => (
              <SlotCell
                key={slot.id}
                slot={{ ...slot, col: 1, row: 1, colSpan: 2, rowSpan: 2 }}
                spreadId={spread.id}
                pageKey={`${spread.id}_double`}
                page={{ key: `${spread.id}_double`, article_text: '' }}
                onPlacementClick={onPlacementClick}
                onLibreClick={onLibreClick}
                onArticleSave={onArticleSave}
                onEditStart={onEditStart}
                onEditEnd={onEditEnd}
                activeDrag={activeDrag}
                isGlobalEditing={isGlobalEditing}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const KkpCdfMagV2Grid = ({
  spreads = [],
  unplaced = [],
  options = [],
  readOnly = false,
  onPlacementMove,
  onSpreadReorder,
  onPageSwap,
  onPlacementClick,
  onLibreClick,
  onArticleSave,
  onOptionalAgendaToggle,
  onMondeFoodLayoutToggle,
}) => {
  const [activeDrag, setActiveDrag] = useState(null);
  const [editingPageKey, setEditingPageKey] = useState(null);

  const beginEdit = useCallback((pageKey) => {
    setEditingPageKey(pageKey);
  }, []);

  const endEdit = useCallback(() => {
    setEditingPageKey(null);
  }, []);

  const isGlobalEditing = editingPageKey !== null || readOnly;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const magazineRef = useRef(null);

  const reorderableByZone = useMemo(() => ({
    magazine: spreads.filter((s) => s.reorderable).map((s) => s.id),
  }), [spreads]);

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    setActiveDrag(null);
    if (!over || !active?.data?.current) return;

    const source = active.data.current;
    const target = over.data?.current;
    if (!target) return;

    if (source.type === 'spread' && target.type === 'reorder-insert') {
      const zone = target.reorderZone || source.reorderZone || 'magazine';
      const zoneOrder = reorderableByZone[zone] || reorderableByZone.magazine || [];
      const targetIndex = computeSpreadInsertIndex(
        source.spreadId,
        target.insertIndex,
        zoneOrder,
      );
      if (targetIndex >= 0) {
        onSpreadReorder(source.spreadId, targetIndex, zone);
      }
      return;
    }

    if (source.type === 'spread' && target.type === 'spread-reorder-target') {
      const zone = source.reorderZone || 'magazine';
      const zoneOrder = reorderableByZone[zone] || reorderableByZone.magazine || [];
      const targetIndex = computeSpreadReorderTargetIndex(
        source.spreadId,
        target.spreadId,
        zoneOrder,
      );
      if (targetIndex >= 0) {
        onSpreadReorder(source.spreadId, targetIndex, zone);
      }
      return;
    }

    if (source.type === 'page-swap' && target.type === 'page-swap-target') {
      if (source.pageKey === target.pageKey) return;
      onPageSwap?.({
        sourcePageKey: source.pageKey,
        targetPageKey: target.pageKey,
      });
      return;
    }

    if (isGlobalEditing) {
      return;
    }

    if (source.type === 'pub' && source.venteId && !source.vente?.locked) {
      if (target.type === 'slot') {
        const targetPage = findCdfPageByKey(spreads, target.pageKey);
        const isTierGridPage = (targetPage?.grid_rows === 3 && (targetPage?.grid_cols ?? 2) === 2);
        const isReservedSwap = isReservedSwapSpread(target.spreadId, spreads)
          && source.spreadId === target.spreadId
          && (target.slotType === 'libre' || target.slotType === 'pub');
        const isThirdPageSwap = isTierGridPage
          && !isReservedSwapSpread(target.spreadId, spreads)
          && (target.slotType === 'libre' || target.slotType === 'pub');
        const isStandardPubExchange = !isReservedSwapSpread(target.spreadId, spreads)
          && !isTierGridPage
          && target.slotType === 'pub';
        if (!isReservedSwap && !isThirdPageSwap && !isStandardPubExchange && target.slotType !== 'libre') {
          return;
        }
        if (source.venteId === target.venteId) return;

        if (target.slotType === 'libre') {
          const page = findCdfPageByKey(spreads, target.pageKey);
          const violation = page
            ? validateHalfPagePlacement(
              page.slots ?? [],
              source.vente?.format,
              target.slotId,
              source.venteId,
            )
            : null;
          if (violation) {
            showViewportNotice(violation);
            return;
          }
        }

        onPlacementMove({
          venteId: source.venteId,
          spreadId: target.spreadId,
          pageKey: target.pageKey,
          slotId: target.slotId,
        });
      }
    }
  }, [onPlacementMove, onSpreadReorder, onPageSwap, reorderableByZone, isGlobalEditing, spreads]);

  const overlayVente = activeDrag?.type === 'pub' ? activeDrag?.vente : null;
  const overlaySpread = activeDrag?.type === 'spread'
    ? spreads.find((s) => s.id === activeDrag.spreadId)
    : null;
  const overlayPageSwap = activeDrag?.type === 'page-swap' ? activeDrag : null;

  const magazineItems = useMemo(
    () => spreads.map((spread) => ({ key: spread.id, spread })),
    [spreads],
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={(e) => {
        if (isGlobalEditing) return;
        setActiveDrag(e.active.data.current);
      }}
      onDragEnd={handleDragEnd}
    >
      {unplaced.length > 0 && (
        <div className="mb-3">
          <div className="small fw-semibold text-secondary-light mb-2">
            Non placées ({unplaced.length})
          </div>
          <div className="kkp-cdf-v2-unplaced">
            {unplaced.map((v) => (
              <UnplacedChip key={v.vente_id} vente={v} disabled={isGlobalEditing} />
            ))}
          </div>
        </div>
      )}

      <div ref={magazineRef} className="kkp-cdf-v2-magazine">
        {magazineItems.map((item) => (
          <SpreadCard
            key={item.key}
            spread={item.spread}
            onPlacementClick={onPlacementClick}
            onLibreClick={onLibreClick}
            onArticleSave={onArticleSave}
            onOptionalAgendaToggle={onOptionalAgendaToggle}
            onMondeFoodLayoutToggle={onMondeFoodLayoutToggle}
            onEditStart={beginEdit}
            onEditEnd={endEdit}
            activeDrag={activeDrag}
            isGlobalEditing={isGlobalEditing}
          />
        ))}
        <SpreadReorderOverlay
          magazineRef={magazineRef}
          spreads={spreads}
          activeDrag={activeDrag}
          reorderableByZone={reorderableByZone}
        />
      </div>

      <div className="kkp-cdf-v2-options">
        <div className="kkp-cdf-v2-options__head">
          <Icon icon="mdi:bookmark-outline" className="text-lg" />
          <span>Options du mois ({options.length})</span>
        </div>
        {options.length > 0 ? (
          <div className="kkp-cdf-v2-options__list">
            {options.map((v) => (
              <button
                key={v.vente_id}
                type="button"
                className="kkp-cdf-v2-options__item"
                style={getKkpSurfaceStyle(v.couleur || v.statut_couleur)}
                onClick={() => onPlacementClick?.({ vente_id: v.vente_id })}
                title="Ouvrir la fiche vente"
              >
                <span className="kkp-cdf-v2-options__format">{v.format}</span>
                <span className="kkp-cdf-v2-options__client">{v.client_label}</span>
                {v.contenu && <span className="kkp-cdf-v2-options__meta">{v.contenu}</span>}
                {v.commercial && <span className="kkp-cdf-v2-options__meta">{v.commercial}</span>}
              </button>
            ))}
          </div>
        ) : (
          <div className="kkp-cdf-v2-options__empty">
            Aucune option pour cette période.
          </div>
        )}
      </div>

      <DragOverlay>
        {overlayVente && (
          <div className="kkp-cdf-v2-chip shadow">
            <span>{overlayVente.format}</span>
            <span className="kkp-cdf-v2-chip__client">{overlayVente.client_label}</span>
          </div>
        )}
        {overlaySpread && (
          <div className="kkp-cdf-v2-spread kkp-cdf-v2-spread--overlay shadow">
            <div className="kkp-cdf-v2-spread__header">
              <Icon icon="mdi:drag" className="kkp-cdf-v2-spread__drag-icon" />
              <span>{overlaySpread.title}</span>
            </div>
          </div>
        )}
        {overlayPageSwap && (
          <div className="kkp-cdf-v2-page-swap-overlay shadow">
            <Icon icon="mdi:swap-horizontal" className="kkp-cdf-v2-page-swap-overlay__icon" />
            <span>p.{overlayPageSwap.pageNumber}</span>
            {overlayPageSwap.defaultLabel && (
              <span className="text-secondary-light"> · {overlayPageSwap.defaultLabel}</span>
            )}
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
};

export default KkpCdfMagV2Grid;
